import { Controller } from "@hotwired/stimulus"
import { escapeHtml } from "../escape_html"

// Adds and removes ingredient rows on the recipe form, and keeps each row's
// unit dropdown in step with the food chosen — "slice" for bread, "tbsp" for
// oil. Units come from the server as a data attribute on each food option, so
// there is no fetch per keystroke.
//
// Removal marks the row's _destroy flag and hides it rather than deleting the
// node, so saved ingredients are actually destroyed server-side. Brand-new
// rows have no persisted record, so those are removed outright.
export default class extends Controller {
  static targets = ["list", "template", "groupTemplate", "row", "recipeFooter", "stepper"]

  // WHICH LADDER A UNIT CLIMBS, mirroring MealPickerHelper#ladder_for_unit.
  //
  // Duplicated rather than fetched: the unit changes as you pick a food or
  // choose from the dropdown, and a round trip to ask the server which
  // rungs to use would put a network hop inside a keystroke. Two arrays and
  // one predicate is the smaller cost — but they are two copies of one
  // decision, so a change to either belongs in both.
  static WEIGHT_LADDER = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90,
    100, 125, 150, 175, 200, 225, 250, 275, 300, 350, 400, 450, 500
  ]

  static AMOUNT_LADDER = [
    0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.33, 1.5, 1.67, 1.75,
    ...Array.from({ length: 29 }, (_, i) => i + 2)
  ]

  static MEASURED_UNITS = ["g", "ml"]

  connect() {
    this.placeGroupFooters()
    this.placeRecipeFooter()
  }

  // The recipe's own "Add ingredient", placed where it actually inserts —
  // above the first heading, at the end of the ungrouped rows.
  //
  // It used to sit at the bottom of the form while inserting at the top,
  // which is the sort of thing you have to learn rather than see. There is no
  // case for adding BELOW the groups: grouping is positional, so a row after
  // the last group belongs to that group, and a genuinely trailing ungrouped
  // row would read as a mistake.
  //
  // With no groups at all there is nothing to sit above, so it stays where it
  // is — which is also the end of the ungrouped rows.
  placeRecipeFooter() {
    const footer = this.recipeFooterTarget
    const firstGroup = this.listTarget.querySelector(".is-group")

    firstGroup ? firstGroup.before(footer) : this.listTarget.append(footer)
  }

  // An "Add to <group>" button at the bottom of each group.
  //
  // Rendered here rather than in the row partial because it has to sit AFTER
  // the group's rows, and rows are flat siblings — a heading's markup cannot
  // wrap what follows it. The button is recreated whenever the list changes,
  // which is cheaper than tracking moves and cannot drift out of step.
  //
  // It is also the only way to add to a particular group. Grouping is
  // positional — a row belongs to whichever heading is above it — so a single
  // button at the end of the list can only ever add to the LAST group, which
  // is the bug this replaces.
  placeGroupFooters() {
    this.element.querySelectorAll(".group-footer").forEach((node) => node.remove())

    for (const heading of this.listTarget.querySelectorAll(".is-group")) {
      // A hidden heading is one marked for destruction — it is still in the
      // DOM so the server destroys it, but it is not a group any more.
      if (heading.style.display === "none") continue

      const name = heading.querySelector("input[name*='[group_name]']")?.value
      const footer = document.createElement("div")

      footer.className = "group-footer"
      footer.innerHTML = `
        <button type="button" data-action="ingredients#addToGroup" class="btn btn-ghost w-full">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
          <span data-label>${this.escape(this.addLabel(name))}</span>
        </button>`

      this.lastRowOf(heading).after(footer)
    }
  }

  // A group renamed mid-edit should say so on its own button.
  //
  // Rewrites the LABEL rather than re-placing the footers: this fires on every
  // keystroke, and replacing the node the button lives in would be fine —
  // except it also runs while the name field has focus, and rebuilding its
  // sibling on each character is work for nothing.
  groupRenamed(event) {
    const heading = event.target.closest("[data-ingredients-target='row']")
    const footer = this.lastRowOf(heading).nextElementSibling

    if (!footer?.classList.contains("group-footer")) return

    const label = footer.querySelector("[data-label]")
    if (label) label.textContent = this.addLabel(event.target.value)
  }

  // The recipe's own ingredients — the ones belonging to no group.
  //
  // Grouping is POSITIONAL: a row belongs to whichever heading is above it,
  // so "the end of the list" is inside the last group the moment a recipe has
  // one. Appending there is what silently filed ingredients under a heading
  // they had nothing to do with.
  //
  // Ungrouped rows live above the first heading, so that is where this puts
  // them. With no groups at all it is simply the end of the list, which is
  // the same place.
  add(event) {
    event.preventDefault()

    // Immediately before the button, which sits at the end of the ungrouped
    // rows — so the new row appears exactly where you clicked.
    const row = this.build(this.templateTarget)

    this.recipeFooterTarget.before(row)
    this.renumber()
    this.focusFirstField(row)
  }

  // Quoted, because a group name is a phrase rather than a noun. "Dressing"
  // reads fine bare, but people write headings the way a recipe does — "For
  // the marinade" — and "Add ingredient to For the marinade" is a sentence
  // that has come apart. Quotes make it a name being referred to.
  addLabel(name) {
    const trimmed = name?.trim()

    return trimmed ? `Add ingredient to “${trimmed}”` : "Add ingredient to this group"
  }

  // One shared implementation — see escape_html.js.
  escape(text) { return escapeHtml(text) }

  // A group heading is just another row in the list, so it orders and moves
  // like any other. Ingredients below it belong to it.
  addGroup(event) {
    event.preventDefault()
    const row = this.append(this.groupTemplateTarget)
    row?.querySelector("input[type=text]")?.focus()
  }

  // Add an ingredient at the END of a group rather than the end of the list.
  //
  // Grouping is POSITIONAL — a row belongs to whichever heading is above it —
  // so "add at the end" always means "add to the last group" once a recipe
  // has any. Each group carries its own button, and the list carries one at
  // the bottom for rows that belong to no group.
  addToGroup(event) {
    event.preventDefault()

    // The button lives in the group's footer, which sits after the group's
    // last row — so the row goes immediately before the footer, and the
    // footer stays at the bottom where it belongs.
    const footer = event.target.closest(".group-footer")
    const row = this.build(this.templateTarget)

    footer.before(row)
    this.renumber()
    this.focusFirstField(row)
  }

  // The ingredient rows a heading owns — everything until the next heading.
  //
  // Hidden rows are skipped: one already marked for destruction is on its
  // way out, and moving it would only renumber a row the server is about to
  // delete.
  rowsUnder(heading) {
    const rows = []
    let next = heading.nextElementSibling

    while (next && !next.classList.contains("is-group")) {
      if (next.matches("[data-ingredients-target='row']") && next.style.display !== "none") {
        rows.push(next)
      }
      next = next.nextElementSibling
    }

    return rows
  }

  // The last row a heading owns — the heading itself when it owns nothing.
  // Footers are skipped: they are furniture, not rows, and the stale ones are
  // being replaced by the caller anyway.
  lastRowOf(heading) {
    let last = heading
    let next = heading.nextElementSibling

    while (next && !next.classList.contains("is-group")) {
      if (next.matches("[data-ingredients-target='row']")) last = next
      next = next.nextElementSibling
    }

    return last
  }

  // NEVER THE RAW <select>, which is what opens the OS picker.
  //
  // This was `input[type=text], select`, and in a new row's markup the
  // select comes FIRST — so querySelector returned it in document order.
  // combobox_controller hides that select on connect, but Stimulus connects
  // asynchronously, so at the moment a row is added it is still visible:
  // tapping "Add ingredient" on a phone opened the native picker showing one
  // blank "Choose a food…" and nothing else, because a blank option is all
  // that select ever holds. Click out and back in and the controller had
  // connected by then, so you got the search box and it worked. Cody,
  // 15 Sep.
  //
  // The combobox's own input by preference, and a text input otherwise —
  // a group heading has one and no select at all.
  focusFirstField(row) {
    const search = row?.querySelector("[data-combobox-target='input']")

    ;(search || row?.querySelector("input[type=text]"))?.focus()
  }

  build(template) {
    const wrapper = document.createElement("div")
    wrapper.innerHTML = template.innerHTML.replace(/NEW_RECORD/g, Date.now())
    return wrapper.firstElementChild
  }

  append(template) {
    const html = template.innerHTML.replace(/NEW_RECORD/g, Date.now())
    this.listTarget.insertAdjacentHTML("beforeend", html)
    this.renumber()
    return this.listTarget.lastElementChild
  }

  // A drag landed. The sortable controller moved the rows; this is the half
  // that says what the new order means.
  //
  // Nothing is saved here — the list belongs to the form, so the change goes
  // with everything else when you save it. That also makes a drag undoable by
  // simply not saving.
  reordered() {
    this.renumber()
  }

  // Position is what the server orders by, so it has to follow the DOM.
  //
  // Also re-places the group footers, since this already runs after every
  // structural change — adding, removing, and dropping a drag. Doing it here
  // rather than at each call site is what stops a footer being left behind by
  // a path that forgot to ask.
  renumber() {
    this.reindex()
    this.placeGroupFooters()
    this.placeRecipeFooter()
  }

  reindex() {
    this.rowTargets.forEach((row, index) => {
      let field = row.querySelector("input[name*='[position]']")
      if (!field) {
        field = document.createElement("input")
        field.type = "hidden"
        const sibling = row.querySelector("input[name*='[kind]']")
        if (!sibling) return
        field.name = sibling.name.replace("[kind]", "[position]")
        row.appendChild(field)
      }
      field.value = index
    })
  }

  // DELETING A SECTION DELETES WHAT IS IN IT, after asking.
  //
  // Membership is positional — there is no group_id, a row below a heading
  // IS in that heading — so the alternatives were both bad. Leaving the rows
  // in place hands them silently to the group above, which is the surprise
  // that started this. Moving them to the root means they must jump ABOVE
  // every group, because that is where root children live: deleting
  // "Dressing" on a real recipe threw nine rows past a screenful of content
  // and read as the whole section vanishing. Cody, 15 Sep: "i deleted a
  // group and the whole thing has disappeared."
  //
  // So it does what deleting a section looks like it does. That destroys
  // rows, which is why it is the one removal here that asks first — and the
  // question names the count, since the rows may well be off-screen.
  remove(event) {
    event.preventDefault()
    const row = event.target.closest("[data-ingredients-target='row']")

    if (row.classList.contains("is-group")) {
      const owned = this.rowsUnder(row)

      if (owned.length) {
        // The app's own dialog, not window.confirm — Turbo's hook is what
        // shared/_confirm_dialog replaces, so borrowing it here gets the
        // same styled dialog every other confirm in the app uses.
        const ask = window.Turbo?.config?.forms?.confirm
        const name = row.querySelector("input[name*='[group_name]']")?.value?.trim()
        const count = `${owned.length} ingredient${owned.length === 1 ? "" : "s"}`
        const message = name
          ? `Delete “${name}” and its ${count}?`
          : `Delete this group and its ${count}?`

        if (ask) {
          Promise.resolve(ask(message)).then((ok) => {
            if (ok) this.destroyGroup(row, owned)
          })
          return
        }
      }

      this.destroyGroup(row, owned)
      return
    }

    this.destroyRow(row)
    this.afterRemoval()
  }

  // A heading and everything under it.
  destroyGroup(heading, owned) {
    for (const row of owned) this.destroyRow(row)
    this.destroyRow(heading)
    this.afterRemoval()
  }

  // Marked for destruction and hidden where the record exists, so the server
  // destroys it; taken out of the DOM outright where it does not, since an
  // unsaved row has nothing to delete.
  destroyRow(row) {
    const destroyField = row.querySelector("[data-ingredients-target='destroy']")

    if (destroyField && destroyField.value !== undefined && row.querySelector("input[name*='[id]']")) {
      destroyField.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
  }

  afterRemoval() {
    this.renumber()

    // Neither branch fires a change event — one sets a hidden field's value
    // in script, the other takes the field out of the document — so the
    // nutrition panel would keep counting an ingredient that is gone.
    //
    // Dispatched from the LIST, not from this controller's element. That
    // element wraps the whole form, so an event bubbling from it travels
    // away from the form rather than through it, and the panel's listener
    // never saw it.
    this.listTarget.dispatchEvent(
      new CustomEvent("ingredients:changed", { bubbles: true })
    )
  }

  foodChanged(event) {
    const select = event.target
    const row = select.closest("[data-ingredients-target='row']")
    this.showWeightNotice(row, select.dataset.weightState)

    const unitSelect = row.querySelector("select[name*='[entered_unit]']")
    if (!unitSelect) return

    // Units now ride on the select itself (set by the combobox when a food is
    // chosen), since the option list no longer holds the whole library.
    const units = select.dataset.units ? JSON.parse(select.dataset.units) : ["g"]

    const previous = unitSelect.value
    unitSelect.innerHTML = ""
    for (const unit of units) {
      const opt = document.createElement("option")
      opt.value = unit
      opt.textContent = unit
      unitSelect.appendChild(opt)
    }

    // Keep the unit only if it was DELIBERATELY chosen and the new food also
    // has it. Testing the value alone silently defeated this: an untouched
    // row reads "g", and almost every food lists grams, so the "keep it"
    // branch always won — pick Olivani, which is measured in tbsp, and the
    // row stayed on grams.
    const keep = unitSelect.dataset.touched === "true" && units.includes(previous)
    unitSelect.value = keep ? previous : units[0]

    // The unit may just have changed from g to tbsp, so the stepper's rungs
    // have to follow it here too — not only when somebody picks from the
    // dropdown themselves.
    this.retuneStepper(unitSelect)

    // Setting .value in script fires nothing, so the styled dropdown over
    // this select would keep showing the old food's unit — and the nutrition
    // panel would not know the unit had changed either.
    //
    // Marked as ours first. unitChosen treats a change event as the user
    // deliberately picking a unit, and this one is the opposite: it is the
    // unit list being replaced BECAUSE the food changed.
    unitSelect.dataset.synthetic = "true"
    unitSelect.dispatchEvent(new Event("change", { bubbles: true }))
    delete unitSelect.dataset.synthetic
  }

  // A change event on the unit select is the user's own doing: the repopulate
  // above sets .value directly, which fires nothing.
  unitChosen(event) {
    // Not the one foodChanged fires to keep the styled dropdown in step —
    // that is the list being REPLACED, which is the opposite of someone
    // choosing from it.
    if (event.target.dataset.synthetic === "true") return

    event.target.dataset.touched = "true"
    this.retuneStepper(event.target)
  }

  // "THIS IS A RAW WEIGHT", the moment a food that needs saying so is
  // picked. The server renders the notice for a food already on the row;
  // a food chosen mid-edit arrives through the combobox, which writes the
  // state onto the select, and this shows the sentence for it. The three
  // sentences are in the notice's own data attributes — recipes/
  // _ingredient_fields owns the copy, not this file.
  //
  // Hidden, not removed, for a food whose weight does not change: the
  // element stays so the next pick can use it.
  //
  // THE RAIL IS THE ROW'S, NOT THE NOTICE'S. has-weight-notice draws the
  // nutrition-coloured line down the left of the whole row, which is what
  // ties the sentence to the ingredient it is about — and the server sets
  // it from the food on the row at render time. A row added mid-edit has
  // no food yet, so it was rendered without the class and never gained it:
  // pick chicken thigh and the sentence appeared with no rail beside it.
  // Cody, 15 Sep. Toggled here, beside the sentence it belongs to, so the
  // two cannot disagree.
  showWeightNotice(row, state) {
    const notice = row?.querySelector("[data-ingredients-target='weightNotice']")
    if (!notice) return

    const sentence = state ? notice.dataset[`sentence${state[0].toUpperCase()}${state.slice(1)}`] : null
    notice.hidden = !sentence
    row.classList.toggle("has-weight-notice", Boolean(sentence))
    if (!sentence) return

    // "This is a raw weight.|Enter what it weighs before cooking." — the
    // first half bold, as the server draws it.
    const [lead, rest] = sentence.split("|")
    const target = notice.querySelector("[data-weight-sentence]")
    target.replaceChildren()
    const strong = document.createElement("strong")
    strong.className = "font-semibold text-ink"
    strong.textContent = lead
    target.append(strong, ` ${rest || ""}`)
  }

  // THE LADDER FOLLOWS THE UNIT.
  //
  // A gram climbs by 5s and 10s where a teaspoon climbs by quarters, so the
  // rungs have to change when the unit does — otherwise picking "g" on a
  // row that was "tsp" leaves the arrows moving a quarter of a gram at a
  // time.
  //
  // Only the attribute is rewritten. stepper_controller reads ladderValue
  // fresh on every step rather than caching it at connect, so there is
  // nothing to re-initialise and no controller change needed.
  retuneStepper(unitSelect) {
    const row = unitSelect.closest("[data-ingredients-target='row']") ||
                unitSelect.closest("li, tr, div")
    const stepper = row?.querySelector("[data-controller~='stepper']")
    if (!stepper) return

    const measured = this.constructor.MEASURED_UNITS.includes(unitSelect.value)
    const ladder = measured ? this.constructor.WEIGHT_LADDER
                            : this.constructor.AMOUNT_LADDER

    stepper.dataset.stepperLadderValue = JSON.stringify(ladder)
    stepper.dataset.stepperMinValue = String(ladder[0])
  }
}
